import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { db } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
} from "firebase/firestore";

// ── CONFIG ────────────────────────────────────────────────────────────────────

const SYMBOLS = ["EURUSD", "GBPUSD", "USDJPY", "XAUUSD", "GER40"];
const TIMEFRAMES = ["M5", "M15", "H1"];

const ETAPAS = [
  { id: 1, nome: "Download dados", icon: "📥", script: "01_download_data" },
  { id: 2, nome: "Pré-processamento", icon: "⚙️", script: "02_preprocess" },
  { id: 3, nome: "Autoencoder", icon: "🧠", script: "03_train_autoencoder" },
  { id: 4, nome: "Treino RL", icon: "🤖", script: "04_train_finrl" },
  { id: 5, nome: "Backtest VectorBT", icon: "📊", script: "05_backtest_vectorbt" },
  { id: 6, nome: "Walk-Forward", icon: "🏛", script: "06_validate_zipline" },
  { id: 7, nome: "Serializar JSON", icon: "📄", script: "07_serialize_strategy" },
  { id: 8, nome: "Salvar Firestore", icon: "☁️", script: "08_save_firestore" },
];

const CRITERIOS = {
  win_rate: { label: "Win Rate", min: 0.58, unit: "%" },
  sharpe_ratio: { label: "Sharpe Ratio", min: 2.0, unit: "" },
  max_drawdown: { label: "Max Drawdown", min: 0.12, unit: "%", inverse: true },
  profit_factor: { label: "Profit Factor", min: 2.0, unit: "" },
};

// ── COMPONENTES ───────────────────────────────────────────────────────────────

function MetricBar({ value, min, inverse, unit }) {
  const pct = Math.min((value / (min * 1.5)) * 100, 100);
  const ok = inverse ? value <= min : value >= min;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-800 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all duration-700 ${
            ok ? "bg-green-500" : "bg-red-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={`text-xs font-mono w-16 text-right ${
          ok ? "text-green-400" : "text-red-400"
        }`}
      >
        {unit === "%" ? `${(value * 100).toFixed(1)}%` : value.toFixed(2)}
      </span>
    </div>
  );
}

function PipelineProgress({ etapaAtual }) {
  return (
    <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2">
      {ETAPAS.map((e, i) => {
        const done = etapaAtual > e.id;
        const current = etapaAtual === e.id;
        const pending = etapaAtual < e.id;
        return (
          <div key={e.id} className="flex items-center gap-1 shrink-0">
            <div
              className={`
              flex flex-col items-center gap-1 px-2 py-1.5 rounded border text-xs transition-all
              ${done ? "bg-green-900/30 border-green-800 text-green-400" : ""}
              ${
                current
                  ? "bg-blue-900/40 border-blue-600 text-blue-300 shadow-[0_0_12px_rgba(59,130,246,0.3)] animate-pulse"
                  : ""
              }
              ${pending ? "bg-gray-900/20 border-gray-800 text-gray-600" : ""}
            `}
            >
              <span>{e.icon}</span>
              <span className="font-mono whitespace-nowrap">
                {e.id}. {e.nome}
              </span>
            </div>
            {i < ETAPAS.length - 1 && (
              <div className={`w-4 h-px ${done ? "bg-green-700" : "bg-gray-800"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function AtivoCard({ symbol, dados, estrategia }) {
  const tfs = TIMEFRAMES.map((tf) => ({
    tf,
    ok: dados?.timeframes?.[tf] || false,
  }));

  const temEstrategia = !!estrategia;

  return (
    <div
      className={`
      border rounded-lg p-4 transition-all duration-500
      ${
        temEstrategia
          ? "border-emerald-700 bg-emerald-900/10 shadow-[0_0_20px_rgba(16,185,129,0.1)]"
          : "border-gray-800 bg-gray-900/50"
      }
    `}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-mono font-bold text-white">{symbol}</span>
          {temEstrategia && (
            <span className="px-2 py-0.5 bg-emerald-900/50 text-emerald-300 border border-emerald-700 rounded text-xs font-mono">
              🏆 APROVADO
            </span>
          )}
        </div>
        <div className="flex gap-1">
          {tfs.map(({ tf, ok }) => (
            <span
              key={tf}
              className={`
              px-1.5 py-0.5 rounded text-xs font-mono border
              ${
                ok
                  ? "bg-green-900/30 text-green-400 border-green-800"
                  : "bg-gray-900/50 text-gray-600 border-gray-800"
              }
            `}
            >
              {tf}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-3">
        {[
          { label: "dados", ok: dados?.raw },
          { label: "ae", ok: dados?.autoencoder },
          { label: "rl", ok: dados?.rl },
          { label: "vbt", ok: dados?.vbt },
          { label: "zip", ok: dados?.zipline },
          { label: "json", ok: dados?.serialized },
          { label: "cloud", ok: temEstrategia },
        ].map(({ label, ok }) => (
          <div
            key={label}
            className={`
            text-center py-1 rounded text-xs font-mono border
            ${
              ok
                ? "bg-green-900/20 text-green-400 border-green-900"
                : "bg-gray-900/30 text-gray-600 border-gray-900"
            }
          `}
          >
            {ok ? "✓" : "·"} {label}
          </div>
        ))}
      </div>

      {temEstrategia && estrategia.validation && (
        <div className="space-y-2 pt-3 border-t border-gray-800">
          {Object.entries(CRITERIOS).map(([key, cfg]) => {
            const val = estrategia.validation[key] ?? 0;
            return (
              <div key={key}>
                <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                  <span>{cfg.label}</span>
                  <span className="text-gray-400">
                    mín: {cfg.unit === "%" ? `${(cfg.min * 100).toFixed(0)}%` : cfg.min}
                    {cfg.unit && cfg.unit !== "%" ? cfg.unit : ""}
                  </span>
                </div>
                <MetricBar
                  value={val}
                  min={cfg.min}
                  unit={cfg.unit}
                  inverse={cfg.inverse}
                />
              </div>
            );
          })}
          <div className="mt-2 pt-2 border-t border-gray-800 grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div>
              <span className="text-gray-600">Direção: </span>
              <span className="text-white font-mono">
                {estrategia.entry_rules?.direction || "—"}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Trades: </span>
              <span className="text-white font-mono">
                {estrategia.validation?.n_trades ?? 0}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Stop: </span>
              <span className="text-white font-mono">
                {((estrategia.exit_rules?.stop_loss_pct || 0) * 100).toFixed(1)}%
              </span>
            </div>
            <div>
              <span className="text-gray-600">Take: </span>
              <span className="text-white font-mono">
                {((estrategia.exit_rules?.take_profit_pct || 0) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LogFeed({ logs }) {
  return (
    <div className="bg-black/60 border border-gray-800 rounded-lg p-4 font-mono text-xs h-48 overflow-y-auto">
      {logs.length === 0 ? (
        <p className="text-gray-600">Aguardando logs...</p>
      ) : (
        logs.map((log, i) => (
          <div
            key={i}
            className={`mb-0.5 ${
              log.type === "success"
                ? "text-green-400"
                : log.type === "error"
                  ? "text-red-400"
                  : log.type === "warning"
                    ? "text-yellow-400"
                    : "text-gray-400"
            }`}
          >
            <span className="text-gray-600">{log.time}</span> {log.msg}
          </div>
        ))
      )}
    </div>
  );
}

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────

export default function Backtest({ user, onLogout }) {
  const [dados, setDados] = useState({});
  const [estrategias, setEstrategias] = useState({});
  const [logs, setLogs] = useState([]);
  const [etapaAtual, setEtapaAtual] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ultimaAtt, setUltimaAtt] = useState(null);
  const [fireErr, setFireErr] = useState(null);

  const addLog = useCallback((msg, type = "info") => {
    const time = new Date().toLocaleTimeString("pt-BR");
    setLogs((prev) => [...prev.slice(-100), { time, msg, type }]);
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setFireErr(null);
    addLog("🔄 Atualizando dados do backtest...");

    try {
      const snap = await getDocs(collection(db, "moanaz_strategies"));
      const ests = {};
      snap.forEach((d) => {
        ests[d.id] = d.data();
      });
      setEstrategias(ests);

      const progSnap = await getDoc(doc(db, "moanaz_backtest", "progress"));
      if (progSnap.exists()) {
        const prog = progSnap.data();
        setDados(prog.symbols || {});
        setEtapaAtual(prog.etapa_atual || 0);
        if (Array.isArray(prog.logs) && prog.logs.length) {
          setLogs(prog.logs.slice(-50));
        }
      }

      const aprovados = Object.keys(ests).length;
      addLog(
        `✅ ${aprovados} estratégia(s) aprovada(s) de ${SYMBOLS.length} ativos`,
        aprovados > 0 ? "success" : "info"
      );
      setUltimaAtt(new Date().toLocaleTimeString("pt-BR"));
    } catch (e) {
      const msg = e?.message || String(e);
      addLog(`❌ Erro ao carregar: ${msg}`, "error");
      setFireErr(msg);
    }

    setLoading(false);
  }, [addLog]);

  useEffect(() => {
    const unsubStrat = onSnapshot(
      collection(db, "moanaz_strategies"),
      (snap) => {
        const ests = {};
        snap.forEach((d) => {
          ests[d.id] = d.data();
        });
        setEstrategias(ests);
        setUltimaAtt(new Date().toLocaleTimeString("pt-BR"));
      },
      (err) => setFireErr(err.message)
    );

    const unsubProg = onSnapshot(
      doc(db, "moanaz_backtest", "progress"),
      (snap) => {
        if (snap.exists()) {
          const prog = snap.data();
          setDados(prog.symbols || {});
          setEtapaAtual(prog.etapa_atual || 0);
          if (Array.isArray(prog.logs) && prog.logs.length) {
            setLogs(prog.logs.slice(-50));
          }
          setUltimaAtt(new Date().toLocaleTimeString("pt-BR"));
        }
      },
      (err) => setFireErr(err.message)
    );

    return () => {
      unsubStrat();
      unsubProg();
    };
  }, []);

  const aprovados = Object.keys(estrategias).length;
  const pct = Math.round((aprovados / SYMBOLS.length) * 100);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-500 hover:text-green-400 font-mono text-sm border border-gray-700 px-3 py-1.5 rounded-lg transition-colors"
            >
              ← Dashboard
            </Link>
            <div>
              <h1 className="text-2xl font-mono font-bold text-white tracking-tight">
                ⬡ MOANAZ <span className="text-green-400">BACKTEST</span>
              </h1>
              <p className="text-gray-500 text-sm font-mono mt-1">
                Laboratório de descoberta autônoma de estratégias
              </p>
              {user?.email && (
                <p className="text-gray-600 text-xs font-mono mt-1">{user.email}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {ultimaAtt && (
              <span className="text-gray-600 text-xs font-mono">
                Última att: {ultimaAtt}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="px-3 py-2 rounded border border-gray-700 text-gray-400 hover:text-red-400 text-xs font-mono"
            >
              sair
            </button>
            <button
              type="button"
              onClick={carregar}
              disabled={loading}
              className={`
                px-4 py-2 rounded border font-mono text-sm transition-all
                ${
                  loading
                    ? "border-gray-800 text-gray-600 cursor-not-allowed"
                    : "border-green-700 text-green-400 hover:bg-green-900/20 hover:shadow-[0_0_12px_rgba(34,197,94,0.2)]"
                }
              `}
            >
              {loading ? "⟳ Atualizando..." : "↻ Atualizar"}
            </button>
          </div>
        </div>

        {fireErr && (
          <p className="text-red-400 text-xs font-mono mb-4 border border-red-900/50 rounded p-2">
            Firestore: {fireErr}
          </p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Ativos", value: SYMBOLS.length, unit: "total" },
            { label: "Aprovados", value: aprovados, unit: "estratégias" },
            { label: "Progresso", value: `${pct}%`, unit: "completo" },
            {
              label: "Etapa atual",
              value: etapaAtual || "—",
              unit: "de 8",
            },
          ].map(({ label, value, unit }) => (
            <div
              key={label}
              className="bg-gray-900/50 border border-gray-800 rounded-lg p-4"
            >
              <p className="text-gray-500 text-xs font-mono mb-1">{label}</p>
              <p className="text-2xl font-mono font-bold text-white">{value}</p>
              <p className="text-gray-600 text-xs font-mono">{unit}</p>
            </div>
          ))}
        </div>

        <div className="mb-8">
          <div className="flex justify-between text-xs font-mono text-gray-500 mb-2">
            <span>Progresso geral</span>
            <span>
              {aprovados}/{SYMBOLS.length} ativos aprovados
            </span>
          </div>
          <div className="bg-gray-800 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(34,197,94,0.5)]"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {etapaAtual > 0 && (
          <div className="mb-8">
            <p className="text-gray-500 text-xs font-mono mb-3">PIPELINE</p>
            <PipelineProgress etapaAtual={etapaAtual} />
          </div>
        )}

        <div className="mb-8">
          <p className="text-gray-500 text-xs font-mono mb-3">ATIVOS</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SYMBOLS.map((sym) => (
              <AtivoCard
                key={sym}
                symbol={sym}
                dados={dados[sym] || {}}
                estrategia={estrategias[sym] || null}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-gray-500 text-xs font-mono mb-3">LOG</p>
          <LogFeed logs={logs} />
        </div>
      </div>
    </div>
  );
}
